import React from 'react';

import translator from 'utils/translator';

import styles from './styles.module.scss';

const _t = (path: string) => translator.text(`homepage.${path}`);

// Set to false to restore the fully expanded version of this section.
const ENABLE_SCROLL_NARRATIVE = true;

function NoCodeSolutions() {
  const items = _t('noCodeSolutions.items') as Array<{ title: string; text: string }>;
  const introStageRef = React.useRef<HTMLDivElement | null>(null);
  const introRef = React.useRef<HTMLDivElement | null>(null);
  const introInnerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLElement | null>>([]);
  const lastScrollYRef = React.useRef(0);
  const lastTransitionAtRef = React.useRef(0);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [isScrollNarrativeActive, setIsScrollNarrativeActive] = React.useState(false);

  React.useEffect(() => {
    let animationFrame = 0;

    const updateActiveItem = () => {
      animationFrame = 0;

      const isMobileViewport = window.innerWidth < 768;
      const shouldEnhance =
        ENABLE_SCROLL_NARRATIVE &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      setIsScrollNarrativeActive(shouldEnhance);
      if (!shouldEnhance) {
        introRef.current?.style.removeProperty('--intro-opacity');
        introInnerRef.current?.style.removeProperty('--intro-entry-offset');
        return;
      }

      if (introStageRef.current && introRef.current && introInnerRef.current) {
        const introStageTop = introStageRef.current.getBoundingClientRect().top;
        const entryStart = window.innerHeight * 0.98;
        const entryEnd = isMobileViewport ? 36 : 64;
        const entryProgress = Math.min(
          Math.max((entryStart - introStageTop) / (entryStart - entryEnd), 0),
          1,
        );
        const easedEntryProgress =
          entryProgress * entryProgress * (3 - 2 * entryProgress);
        const entryOffsetStart = isMobileViewport
          ? -Math.min(Math.max(window.innerHeight * 0.15, 96), 128)
          : -Math.min(Math.max(window.innerHeight * 0.3, 220), 280);
        const entryOffsetDistance = isMobileViewport
          ? Math.min(Math.max(window.innerHeight * 0.23, 160), 200)
          : Math.min(Math.max(window.innerHeight * 0.36, 270), 330);
        const entryOffset = entryOffsetStart + easedEntryProgress * entryOffsetDistance;
        const entryOpacity = Math.min(Math.max((entryProgress - 0.02) / 0.42, 0), 1);
        const fadeStart = isMobileViewport
          ? Math.min(Math.max(window.innerHeight * 0.19, 140), 170)
          : Math.min(Math.max(window.innerHeight * 0.22, 170), 210);
        const fadeEnd = isMobileViewport ? 36 : 72;
        const introTop = introInnerRef.current.getBoundingClientRect().top;
        const introProgress = Math.min(
          Math.max((fadeStart - introTop) / (fadeStart - fadeEnd), 0),
          1,
        );

        introInnerRef.current.style.setProperty('--intro-entry-offset', `${entryOffset}px`);
        introRef.current.style.setProperty(
          '--intro-opacity',
          String(entryOpacity * (1 - introProgress)),
        );
      }

      const currentScrollY = window.scrollY;
      const previousScrollY = lastScrollYRef.current;
      const scrollDirection = currentScrollY >= previousScrollY ? 1 : -1;
      const scrollDistance = Math.abs(currentScrollY - previousScrollY);
      const activationPoint = isMobileViewport
        ? Math.min(Math.max(window.innerHeight * 0.46, 300), 360)
        : Math.min(Math.max(window.innerHeight * 0.42, 330), 390);
      const transitionCooldown = isMobileViewport ? 220 : 280;
      let closestIndex = -1;

      itemRefs.current.forEach((item, index) => {
        if (item && item.getBoundingClientRect().top <= activationPoint) closestIndex = index;
      });

      setActiveIndex((currentIndex) => {
        const now = performance.now();
        const currentItem = itemRefs.current[currentIndex];
        const currentItemRect = currentItem?.getBoundingClientRect();
        const indexDrift = Math.abs(closestIndex - currentIndex);
        const activeItemHasLeftReadingArea =
          currentItemRect &&
          (scrollDirection < 0
            ? currentItemRect.top > window.innerHeight * (isMobileViewport ? 0.78 : 0.74)
            : currentItemRect.bottom < window.innerHeight * (isMobileViewport ? 0.14 : 0.2));

        // Keep the mobile narrative tied to the viewport on every frame. A cooldown
        // can otherwise leave the previous item active after a quick direction change.
        if (isMobileViewport && closestIndex !== currentIndex) {
          lastTransitionAtRef.current = now;
          return closestIndex;
        }

        // Recover immediately when fast scrolling or a direction change leaves the
        // visual state behind the viewport. Normal scrolling still advances one item
        // at a time, but the cooldown can no longer leave the narrative temporarily blank.
        if (
          closestIndex !== currentIndex &&
          (scrollDistance > window.innerHeight * 0.7 ||
            indexDrift > 1 ||
            activeItemHasLeftReadingArea)
        ) {
          lastTransitionAtRef.current = now;
          return closestIndex;
        }

        if (now - lastTransitionAtRef.current < transitionCooldown) return currentIndex;

        if (scrollDirection > 0) {
          const nextIndex = currentIndex + 1;
          const nextItem = itemRefs.current[nextIndex];

          if (nextItem && nextItem.getBoundingClientRect().top <= activationPoint) {
            lastTransitionAtRef.current = now;
            return nextIndex;
          }
        } else if (currentIndex >= 0) {
          if (
            currentItem &&
            currentItem.getBoundingClientRect().top > activationPoint + 88
          ) {
            lastTransitionAtRef.current = now;
            return currentIndex - 1;
          }
        }

        return currentIndex;
      });

      lastScrollYRef.current = currentScrollY;
    };

    const scheduleUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(updateActiveItem);
    };

    updateActiveItem();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  const itemClassName = (index: number) => {
    let narrativeState = '';

    if (isScrollNarrativeActive) {
      if (index === activeIndex) narrativeState = styles.itemActive;
      else if (index === activeIndex + 1) narrativeState = styles.itemNext;
      else if (index < activeIndex) narrativeState = styles.itemPast;
      else narrativeState = styles.itemQueued;
    }

    return [styles.item, narrativeState].filter(Boolean).join(' ');
  };

  return (
    <section className={`${styles.noCodeSolutions} flex flex-col w-full items-center`}>
      <div
        className={`${styles.content} ${isScrollNarrativeActive ? styles.scrollExperience : ''}`}
      >
        <div className={styles.introStage} ref={introStageRef}>
          <div className={styles.intro} ref={introRef}>
            <div className={styles.introInner} ref={introInnerRef}>
              <h2 className={styles.title}>{_t('noCodeSolutions.title')}</h2>
              <p className={styles.subtitle}>{_t('noCodeSolutions.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className={styles.capabilities}>
          <div className={`${styles.items} ${isScrollNarrativeActive ? styles.scrollNarrative : ''}`}>
            {items.map((item, index) => (
              <article
                className={itemClassName(index)}
                key={item.title}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
              >
                <div className={styles.itemContent}>
                  <span className={styles.itemIndex} aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className={styles.itemTitle}>{item.title}</h3>
                    <div className={styles.itemBody}>
                      <div className={styles.itemBodyInner}>
                        <p>{item.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
            <article
              className={itemClassName(items.length)}
              ref={(element) => {
                itemRefs.current[items.length] = element;
              }}
            >
              <div className={styles.itemContent}>
                <span className={`${styles.itemIndex} ${styles.supportMarker}`} aria-hidden="true">
                  ✦
                </span>
                <div>
                  <h3 className={styles.itemTitle}>{_t('noCodeSolutions.supportTitle')}</h3>
                  <div className={styles.itemBody}>
                    <div className={styles.itemBodyInner}>
                      <p>{_t('noCodeSolutions.supportText')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

export default NoCodeSolutions;
